import { baseApi } from "@/services/api";

export type ScreensaverRecord = {
  id: string;
  versionName?: string;
  version?: string;
  filename?: string;
  mediaUrl?: string;
  apkUrl?: string;
  sha256?: string;
  contentId?: string;
  widthPx?: number | null;
  heightPx?: number | null;
  displayProfileId?: string | null;
  codec?: string | null;
  hasAudio?: boolean;
  approvalState?: string;
  createdAt?: string;
};

const apiWithTags = baseApi.enhanceEndpoints({});

export function buildScreensaverSignPath(): string {
  return "v1/sa/device-fleet/screensaver/sign";
}

export function buildScreensaversPath(): string {
  return "v1/sa/device-fleet/screensavers";
}

export function buildScreensaverAssignmentPath(): string {
  return "v1/sa/device-fleet/screensaver-assignment";
}

export function buildBrandScreensaverAssignmentPath(brandId: string): string {
  return `v1/sa/device-fleet/screensaver-assignment/brands/${encodeURIComponent(brandId)}`;
}

export type ScreensaverAsset = {
  releaseId: string;
  versionName: string | null;
  versionCode: number | null;
  contentId: string | null;
  mediaUrl: string | null;
  sha256: string | null;
  filename: string | null;
  approvalState?: string | null;
};

export type ScreensaverAssignmentRollout = {
  fanoutEnabled: boolean;
  deviceCommands?: string;
  infersOverridesFromDevices?: boolean;
};

export type GlobalScreensaverAssignment = {
  success?: boolean;
  source: "global";
  sourceLabel: string;
  pointer: "builtin" | "stored";
  effective: ScreensaverAsset | null;
  versionName: string | null;
  inheritingBrandCount: number | null;
  inheritingBrandCountStatus?: string;
  overrideBrandCount?: number;
  rollout?: ScreensaverAssignmentRollout;
};

export type BrandScreensaverAssignment = {
  success?: boolean;
  brandId: string;
  source: "global" | "brand";
  sourceLabel: string;
  effective: ScreensaverAsset | null;
  versionName: string | null;
  overrideReleaseId: string | null;
  globalReleaseId: string;
  globalPointer: "builtin" | "stored";
  rollout?: ScreensaverAssignmentRollout;
};

export function buildScreensaverCommandBody(args: {
  action: "apply" | "enable" | "disable";
  screensaverId?: string;
}): {
  command: "update_kk_screensaver";
  action: "apply" | "enable" | "disable";
  screensaverId?: string;
} {
  const body: {
    command: "update_kk_screensaver";
    action: "apply" | "enable" | "disable";
    screensaverId?: string;
  } = {
    command: "update_kk_screensaver",
    action: args.action,
  };
  if (args.action === "apply" && args.screensaverId) {
    body.screensaverId = args.screensaverId;
  }
  return body;
}

/** Screensaver apply/enable/disable is exactly one Zone (deviceId). */
export function canTargetSingleZone(deviceIds: string[]): boolean {
  return Array.isArray(deviceIds) && deviceIds.length === 1;
}

export const screensaversApi = apiWithTags.injectEndpoints({
  endpoints: (builder) => ({
    listScreensavers: builder.query<{ screensavers: ScreensaverRecord[] }, void>({
      query: () => buildScreensaversPath(),
      providesTags: ["Screensavers"],
    }),
    signScreensaverUpload: builder.mutation<
      { url: string; key: string; expiresIn: number },
      { filename: string }
    >({
      query: ({ filename }) => ({
        url: buildScreensaverSignPath(),
        method: "POST",
        body: { filename },
      }),
    }),
    createScreensaver: builder.mutation<
      { success: boolean; screensaver: ScreensaverRecord },
      {
        filename: string;
        name?: string;
        displayProfileId?: string;
        description?: string;
      }
    >({
      query: (body) => ({
        url: buildScreensaversPath(),
        method: "POST",
        body,
      }),
      invalidatesTags: ["Screensavers"],
    }),
    getGlobalScreensaverAssignment: builder.query<GlobalScreensaverAssignment, void>({
      query: () => buildScreensaverAssignmentPath(),
      providesTags: ["ScreensaverAssignment"],
    }),
    setGlobalScreensaverAssignment: builder.mutation<
      GlobalScreensaverAssignment,
      { releaseId: string }
    >({
      query: ({ releaseId }) => ({
        url: buildScreensaverAssignmentPath(),
        method: "PUT",
        body: { releaseId },
      }),
      invalidatesTags: ["ScreensaverAssignment"],
    }),
    getBrandScreensaverAssignment: builder.query<BrandScreensaverAssignment, string>({
      query: (brandId) => buildBrandScreensaverAssignmentPath(brandId),
      providesTags: ["ScreensaverAssignment"],
    }),
    setBrandScreensaverAssignment: builder.mutation<
      BrandScreensaverAssignment,
      { brandId: string; releaseId: string }
    >({
      query: ({ brandId, releaseId }) => ({
        url: buildBrandScreensaverAssignmentPath(brandId),
        method: "PUT",
        body: { releaseId },
      }),
      invalidatesTags: ["ScreensaverAssignment"],
    }),
    clearBrandScreensaverAssignment: builder.mutation<BrandScreensaverAssignment, string>({
      query: (brandId) => ({
        url: buildBrandScreensaverAssignmentPath(brandId),
        method: "DELETE",
      }),
      invalidatesTags: ["ScreensaverAssignment"],
    }),
  }),
});

export const {
  useListScreensaversQuery,
  useSignScreensaverUploadMutation,
  useCreateScreensaverMutation,
  useGetGlobalScreensaverAssignmentQuery,
  useSetGlobalScreensaverAssignmentMutation,
  useGetBrandScreensaverAssignmentQuery,
  useSetBrandScreensaverAssignmentMutation,
  useClearBrandScreensaverAssignmentMutation,
} = screensaversApi;
